// Passos do tour guiado (Intro.js). Dois tutoriais em sequência, os mesmos nomes de `user_tutorial`:
// `navbar` (barra superior) e `sidebar` (menu lateral e RushMind).
//
// Regras:
// - O tour é curto e conduz ao hábito central: abrir a tarefa e usar o timer para lançar hora.
// - Passo cujo alvo não existe ou está escondido (menu que o papel não vê, item que só aparece no
//   desktop) é descartado em tempo de execução por `useTutorial`, nunca vira balão solto.
// - Texto sem travessão, em pt-BR. `introUser` substitui `intro` para quem não é admin nem gestor.

export type TourUser = {
  administrator?: number | boolean
  manager?: number | boolean
  external_user?: number | boolean
}

export type TourStep = {
  element: string
  title: string
  intro: string
  introUser?: string
}

export type TutorialName = 'navbar' | 'sidebar'

export const navbarSteps: TourStep[] = [
  {
    element: '#navbar-new-task',
    title: 'Nova tarefa',
    intro:
      'Crie uma tarefa de qualquer tela. É o caminho mais rápido para registrar o que vai ser feito.',
  },
  {
    element: '#navbar-notifications',
    title: 'Notificações',
    intro: 'Menções, prazos e avisos do sistema chegam aqui.',
  },
  {
    element: '#navbar-helper',
    title: 'Ajuda',
    intro:
      'Precisa de ajuda? Aqui você refaz este tour, revê a introdução e abre a Central de ajuda.',
  },
  {
    element: '#navbar-avatar',
    title: 'Sua conta',
    intro:
      'Perfil, tema e ajuda ficam neste menu. No celular, é aqui que você encontra "Refazer tour".',
  },
]

export const sidebarSteps: TourStep[] = [
  {
    element: '#sidebar-dashboard',
    title: 'Dashboard',
    intro: 'Uma visão geral da empresa: projetos, equipe e o que o RushMind destaca para você.',
    introUser: 'Sua visão do dia: tarefas, horas lançadas e o que o RushMind destaca para você.',
  },
  {
    element: '#sidebar-equipes-e-pessoas',
    title: 'Equipes e pessoas',
    intro: 'Cadastre e organize as pessoas e as equipes da sua empresa.',
  },
  {
    element: '#sidebar-projetos',
    title: 'Projetos',
    intro: 'Todos os projetos ficam aqui. Crie um novo ou abra um existente para ver as tarefas.',
  },
  {
    element: '#sidebar-minhas-tarefas',
    title: 'Minhas tarefas',
    intro:
      'Sua lista de trabalho. Abra uma tarefa e use o botão de play para iniciar o timer: a hora é lançada quando você pausa.',
    introUser:
      'Comece por aqui. Abra uma tarefa e use o botão de play para iniciar o timer: a hora é lançada quando você pausa.',
  },
  {
    element: '#sidebar-timetracking',
    title: 'Time Tracking',
    intro: 'Acompanhe as horas lançadas pela equipe.',
    introUser:
      'Confira as horas que você já lançou. Elas aparecem aqui assim que você pausa o timer.',
  },
  {
    element: '#sidebar-calendario',
    title: 'Calendário',
    intro: 'Veja tarefas e prazos no calendário, incluindo suas agendas externas.',
  },
  {
    element: '.rushmind-widget',
    title: 'RushMind',
    intro: 'Seu assistente de IA. Ele destaca o que merece atenção e lembra do que está pendente.',
  },
]

const STEPS_BY_TUTORIAL: Record<TutorialName, TourStep[]> = {
  navbar: navbarSteps,
  sidebar: sidebarSteps,
}

const isManagerLike = (user?: TourUser | null) => !!user?.administrator || !!user?.manager

// Resolve os passos do tutorial para o papel do usuário (sem olhar o DOM, isso é feito depois).
export const getTutorialSteps = (
  name: string | undefined,
  user?: TourUser | null
): Array<{element: string; title: string; intro: string}> => {
  const steps = name ? STEPS_BY_TUTORIAL[name as TutorialName] : undefined
  if (!steps) return []

  return steps
    .filter((step) => {
      // Busca e criação de tarefa não existem para o usuário externo.
      if (user?.external_user && step.element === '#navbar-new-task') return false
      return true
    })
    .map(({element, title, intro, introUser}) => ({
      element,
      title,
      intro: !isManagerLike(user) && introUser ? introUser : intro,
    }))
}
